import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { PaginatorCurrentPageReportOptions } from 'primereact/paginator';
import { useAppDispatch, useAppSelector } from '@core/store/hook.ts';
import { loadAuditLogs } from '@core/actions/auditLogActions.ts';
import {
  auditLogs,
  auditLoading,
  auditPage,
  auditSize,
  auditTotal,
  auditFilterAction,
  auditFilterUsername,
  auditFilterFrom,
  auditFilterTo,
  setAuditPage,
  setAuditSize,
  setAuditFilterAction,
  setAuditFilterUsername,
  setAuditFilterFrom,
  setAuditFilterTo,
  resetAuditFilters,
} from '@core/slice/AuditLogSlice.ts';
import { FetchStatusType } from '@core/modeles/model.ts';
import { userConnected } from '@core/slice/UsersSlice.ts';
import { TypeProfil } from '@modules/plaintes/plaintes.model.ts';
import Forbidden403 from '@components/Forbiden403.tsx';
import Spinner from '@shared/Spinner.tsx';


const ROLE_LABELS: Record<string, string> = {
  // Rôles d'administration du royaume
  'realm-admin': 'Administrateur du royaume',
  'view-realm': 'Voir le royaume',
  'manage-realm': 'Gérer le royaume',
  'query-realms': 'Interroger les royaumes',

  // Rôles utilisateurs
  'view-users': 'Voir les utilisateurs',
  'manage-users': 'Gérer les utilisateurs',
  'query-users': 'Interroger les utilisateurs',
  'impersonation': 'Personnification',

  // Rôles clients
  'view-clients': 'Voir les clients',
  'manage-clients': 'Gérer les clients',
  'query-clients': 'Interroger les clients',
  'create-client': 'Créer un client',

  // Rôles fournisseurs d'identité
  'view-identity-providers': 'Voir les fournisseurs d\'identité',
  'manage-identity-providers': 'Gérer les fournisseurs d\'identité',

  // Rôles autorisations
  'view-authorization': 'Voir les autorisations',
  'manage-authorization': 'Gérer les autorisations',

  // Rôles événements
  'view-events': 'Voir les événements',
  'manage-events': 'Gérer les événements',

  // Rôles groupes
  'query-groups': 'Interroger les groupes',

  // Rôles compte utilisateur
  'manage-account': 'Gérer le compte',
  'manage-account-links': 'Gérer les liens du compte',
  'view-profile': 'Voir le profil',

  // Rôles spéciaux Keycloak
  'offline_access': 'Accès hors ligne',
  'uma_authorization': 'Autorisation UMA',
  'default-roles-master': 'Rôles par défaut (master)',
  'default-roles-mps': 'Rôles par défaut (MPS)',
  'broker': 'Courtier d\'identité',
  'manage-consent': 'Gérer les consentements',
  'view-consent': 'Voir les consentements',
  'delete-account': 'Supprimer le compte',
  'view-applications': 'Voir les applications',
  'view-groups': 'Voir les groupes',

  // Rôles applicatifs MPPSPF (ProfilEntity.Type)
  'ADMIN': 'Administrateur',
  'INTERNAL': 'Agent interne',
  'EXTERNAL': 'Partenaire externe',
  'COMPETENT_ENTITY': 'Entité compétente',
  'RGS': 'Agent RGS',
  'PFM': 'Point Focal Ministériel',
  'CGP': 'Coordinateur de Gestion des Plaintes',
  'ADMIN_DRPS': 'Administrateur DRPS',
  'ADMIN_PSRC': 'Administrateur PSRC',
  'COMMUNICATION': 'Communication',
  'DGRSU': 'Agent DGRSU',
};

function formatRoleToFrench(raw: string): string {
  const key = raw.trim();
  if (ROLE_LABELS[key]) return ROLE_LABELS[key];
  // Fallback : remplace les tirets/underscores par des espaces et capitalise
  return key
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const RoleCell: React.FC<{ role: string }> = ({ role }) => {
  const [expanded, setExpanded] = useState(false);

  if (!role) return <span className="text-gray-400">—</span>;

  const roles = role.split(',').map((r) => formatRoleToFrench(r));
  const text = roles.join(', ');

  return (
    <div className="text-sm text-gray-700">
      <div
        style={
          !expanded
            ? {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as const,
              }
            : undefined
        }
      >
        {text}
      </div>
      <button
        className="text-xs text-blue-500 hover:underline mt-1 focus:outline-none"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? 'Voir moins' : 'Voir plus'}
      </button>
    </div>
  );
};

const ACTION_LABELS: Record<string, string> = {
  CREATE_USER: 'Création utilisateur',
  EDIT_USER: 'Modification utilisateur',
  DELETE_USER: 'Suppression utilisateur',
  CREATE_PROFILE: 'Création groupe',
  EDIT_PROFILE: 'Modification groupe',
  DELETE_PROFILE: 'Suppression groupe',
  CREATE_COMPLAINT: 'Création plainte',
  EDIT_COMPLAINT: 'Modification plainte',
  CHANGE_STATUS_COMPLAINT: 'Changement statut plainte',
  LOGIN: 'Connexion',
  LOGOUT: 'Déconnexion',
  OTHER: 'Autre',   
};

const ACTION_SEVERITY: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  CREATE_USER: 'success',
  EDIT_USER: 'info',
  DELETE_USER: 'danger',
  CREATE_PROFILE: 'success',
  EDIT_PROFILE: 'info',
  DELETE_PROFILE: 'danger',
  CREATE_COMPLAINT: 'success',
  EDIT_COMPLAINT: 'info',
  CHANGE_STATUS_COMPLAINT: 'warning',
  LOGIN: 'info',
  LOGOUT: 'warning',
  OTHER: 'info', 
};

const ACTION_OPTIONS = [
  { label: 'Toutes les actions', value: '' },
  ...Object.entries(ACTION_LABELS).map(([value, label]) => ({ label, value })),
];

const AuditLogPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const connectedUser = useAppSelector(userConnected);
  const logs = useAppSelector(auditLogs);
  const loading = useAppSelector(auditLoading);
  const page = useAppSelector(auditPage);
  const size = useAppSelector(auditSize);
  const filterAction = useAppSelector(auditFilterAction);
  const filterUsername = useAppSelector(auditFilterUsername);
  const filterFrom = useAppSelector(auditFilterFrom);
  const filterTo = useAppSelector(auditFilterTo);

  // Seul le Super Admin (TypeProfil.ADMIN) peut accéder au journal
  const isSuperAdmin = connectedUser?.profil?.type === TypeProfil.ADMIN;

  const fetchLogs = useCallback(() => {
    dispatch(
      loadAuditLogs({
        page,
        size,
        action: filterAction || undefined,
        username: filterUsername || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      })
    );
  }, [dispatch, page, size, filterAction, filterUsername, filterFrom, filterTo]);

  useEffect(() => {
    if (isSuperAdmin) fetchLogs();
  }, [fetchLogs, isSuperAdmin]);

  const displayTotal = useAppSelector(auditTotal);

  const paginatorTemplate = useMemo(() => ({
    layout: 'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
    CurrentPageReport: ({ rows }: PaginatorCurrentPageReportOptions) => {
      const first = page * rows + 1;
      const last = Math.min(first + rows - 1, displayTotal);
      return (
        <span className="text-sm text-gray-600 mr-2">
          Afficher {displayTotal === 0 ? 0 : first} à {Math.min(last, displayTotal)} sur {displayTotal} lignes
        </span>
      );
    },
  }), [page, size, displayTotal]);

  if (!isSuperAdmin) {
    return <Forbidden403 />;
  }

  const displayData = logs?.data ?? [];

  const actionTemplate = (row: any) => (
    <Tag
      severity={ACTION_SEVERITY[row.action] ?? 'info'}
      value={ACTION_LABELS[row.action] ?? row.action}
    />
  );

  const timestampTemplate = (row: any) => {
    const d = new Date(row.timestamp);
    return (
      <span>
        {d.toLocaleDateString('fr-FR')}{' '}
        <span className="text-gray-400 text-sm">
          {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      </span>
    );
  };

  const detailsTemplate = (row: any) => (
    <span className="text-sm text-gray-600">{row.details ?? '—'}</span>
  );

  const targetTemplate = (row: any) =>
    row.targetId ? (
      <span className="font-mono text-xs bg-gray-100 px-1 rounded">
        {row.targetType ? `[${row.targetType}] ` : ''}{row.targetId}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );

  const ipTemplate = (row: any) => (
    <span className="text-xs text-gray-500 font-mono">{row.ipAddress ?? '—'}</span>
  );

  const auditActions: string[] = [
    'CREATE_USER',
    'EDIT_USER',
    'DELETE_USER',
    'CREATE_PROFILE',
    'EDIT_PROFILE',
    'DELETE_PROFILE',
    'LOGIN',
    'LOGOUT',
    'CREATE_COMPLAINT',
    'EDIT_COMPLAINT',
    'CHANGE_STATUS_COMPLAINT',
    'OTHER',
  ];

  const isAuditAction = (value: string): boolean => {
    return auditActions.includes(value);
  };

  return (
    <div className="p-4 w-full min-w-0" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* En-tête */}

      {/* Filtres */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
        <Dropdown
          value={filterAction}
          options={ACTION_OPTIONS}
          onChange={(e) => {
            dispatch(setAuditFilterAction(isAuditAction(e.value) ? e.value as string : ''));
          }}
          placeholder="Type d'action"
          className="w-full"
        />
        <InputText
          value={filterUsername}
          onChange={(e) => dispatch(setAuditFilterUsername(e.target.value))}
          placeholder="Nom d'utilisateur"
          className="w-full"
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Du</label>
          <input
            type="datetime-local"
            value={filterFrom}
            onChange={(e) => dispatch(setAuditFilterFrom(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Au</label>
          <input
            type="datetime-local"
            value={filterTo}
            onChange={(e) => dispatch(setAuditFilterTo(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
          />
        </div>
        <div className="md:col-span-4 flex justify-end">
          <Button
            label="Réinitialiser"
            icon="pi pi-filter-slash"
            text
            size="small"
            onClick={() => dispatch(resetAuditFilters())}
          />
        </div>
      </div>

      {/* Tableau — lecture seule, sans action de suppression */}
      {loading === FetchStatusType.LOADING ? (
        <div className="flex justify-center items-center py-16">
          <Spinner />
        </div>
      ) : (
        <DataTable
          value={displayData}
          lazy
          paginator
          rows={size}
          totalRecords={displayTotal}
          first={page * size}
          onPage={(e) => {
            dispatch(setAuditPage(e.page ?? 0));
            if (e.rows !== size) dispatch(setAuditSize(e.rows));
          }}
          emptyMessage="Aucune action enregistrée."
          className="text-sm w-full"
          tableStyle={{ width: '100%' }}
          rowHover
          stripedRows
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate={paginatorTemplate}
          paginatorClassName="flex items-center justify-end"
        >
          <Column
            field="timestamp"
            header="Date / Heure"
            body={timestampTemplate}
            style={{ minWidth: '160px' }}
          />
          <Column
            field="action"
            header="Action"
            body={actionTemplate}
            style={{ minWidth: '190px' }}
          />
          <Column
            field="username"
            header="Utilisateur"
            style={{ minWidth: '140px' }}
          />
          <Column
            field="role"
            header="Rôle"
            body={(row) => <RoleCell role={row.role} />}
            style={{ minWidth: '200px' }}
          />
          <Column
            header="Objet concerné"
            body={targetTemplate}
            style={{ minWidth: '160px' }}
          />
          <Column
            field="details"
            header="Détails"
            body={detailsTemplate}
            style={{ minWidth: '200px' }}
          />
          <Column
            header="Adresse IP"
            body={ipTemplate}
            style={{ minWidth: '130px' }}
          />
        </DataTable>
      )}
    </div>
  );
};

export default AuditLogPage;

